import { clsx } from 'clsx'
import { IconButton } from 'component/Button/IconButton'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useBodyScrollLock } from 'hook/useBodyScrollLock'
import { useLatestCallback } from 'hook/useLatestCallback'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import css from './Banner.module.scss'

const HANDLE = 'handle'

const isTouchDevice = () => {
  try {
    document.createEvent('TouchEvent')
    return true
  } catch (e) {
    return false
  }
}

type Props = {
  beforeClose?(): boolean
  bottom?: ReactNode
  children: ReactNode
  className?: string
  fullscreen?: boolean
  id?: string
  onCancel?(): void
  scroll?: boolean
  tall?: boolean
  title?: string
  subtitle?: string
  customHeader?: ReactNode
  canClose?: boolean
  hide?: boolean
}

export const Banner = observer((props: Props) => {
  const {
    beforeClose,
    bottom,
    children,
    fullscreen,
    id,
    onCancel,
    tall,
    title,
    subtitle,
    customHeader,
    canClose = true,
  } = props
  const hide = props.hide === true
  const scroll = props.scroll ?? true
  const [closed, setClosed] = useState(true)
  const [display, setDisplay] = useState(true)
  const className = clsx(
    props.className,
    css.banner,
    fullscreen && css.fullscreen,
    !canClose && css.unused,
    tall && css.tall,
    closed ? css.close : css.open,
    display ? null : css.hide,
  )
  const ref = useRef<HTMLDivElement>()
  const timeout = useRef<TimeoutId>()
  const delay = 200

  useBodyScrollLock(!hide)

  useEffect(() => {
    setTimeout(() => requestAnimationFrame(() => transit(hide)), 33)
  }, [hide])

  useEffect(() => {
    clearTimeout(timeout.current)
    if (closed) timeout.current = setTimeout(() => setDisplay(false), 200)
    else setDisplay(true)
  }, [closed])

  useEffect(() => {
    const container = ref.current?.querySelector(
      '[data-overlayscrollbars-contents]',
    )
    if (container) container.scrollTo({ left: 0, top: 0 })
  }, [id])

  const transit = useLatestCallback((close: boolean) => {
    if (!close) {
      setClosed(false)
      return
    }
    if (beforeClose) {
      close = beforeClose()
      if (!close && onCancel) {
        setClosed(false)
        onCancel()
        return
      }
    }
    setClosed(close)
    if (close && onCancel) setTimeout(() => onCancel(), delay)
  })

  const setRef = useLatestCallback((element: HTMLDivElement | null) => {
    ref.current = element ?? undefined
    if (!element) return

    const focus =
      element.querySelector('input') ?? element.querySelector('button')
    focus?.focus()

    const handle = element.querySelector('.' + HANDLE) as HTMLDivElement
    if (!handle) return

    if (!canClose) return

    const touch = isTouchDevice()
    const height = element.clientHeight
    const limit = height / 2
    const shadow = document.createElement('div')
    shadow.classList.add(clsx(css.shadow))
    const drag = {
      element,
      active: false,
      shadow: false,
      transform: false,
      startX: 0,
      startY: 0,
      endY: 0,
    }

    function onDragStart(x: number, y: number) {
      drag.active = true
      drag.transform = false
      drag.startX = x
      drag.startY = y
      drag.endY = y
    }

    function onDragMove(x: number, y: number) {
      drag.endY = y
      const dx = x - drag.startX
      const dy = y - drag.startY
      const ty = Math.max(0, dy)
      if (!drag.transform) {
        if (Math.abs(dx) > 20) {
          drag.active = false
          return
        }
        if (Math.abs(dy) > 20) {
          drag.transform = true
        } else {
          return
        }
      }

      const transform = `translateY(${ty}px)`
      requestAnimationFrame(() => (drag.element.style.transform = transform))
      if (!drag.shadow) {
        drag.shadow = true
        document.body.append(shadow)
      }
    }

    function onDragEnd() {
      const dy = Math.max(0, drag.endY - drag.startY)
      const close = dy > limit
      drag.active = false
      drag.shadow = false
      shadow.remove()
      if (!close) requestAnimationFrame(() => (drag.element.style.transform = ''))
      transit(close)
    }

    if (touch) {
      handle.addEventListener('touchstart', (event) => {
        const x = event.touches[0]?.clientX ?? 0
        const y = event.touches[0]?.clientY ?? 0
        onDragStart(x, y)
      })
      window.addEventListener('touchmove', (event) => {
        const x = event.touches[0]?.clientX ?? 0
        const y = event.touches[0]?.clientY ?? 0
        if (drag.active) onDragMove(x, y)
      })
      window.addEventListener('touchend', (_) => {
        if (drag.active) onDragEnd()
      })
    } else {
      handle.addEventListener('mousedown', (event) => {
        const x = event.clientX
        const y = event.clientY
        onDragStart(x, y)
      })
      window.addEventListener('mousemove', (event) => {
        const x = event.clientX
        const y = event.clientY
        if (drag.active) onDragMove(x, y)
      })
      window.addEventListener('mouseup', (_) => {
        if (drag.active) onDragEnd()
      })
    }
  })

  const onClickClose = useLatestCallback(() => {
    transit(true)
  })

  const onClickShadow = useLatestCallback((event: MouseEvent) => {
    const shadow = event.target === event.currentTarget
    if (shadow && canClose) onClickClose()
  })

  const close = (
    <IconButton size={24} action={onClickClose}>
      <Icon name="close" size={20} />
    </IconButton>
  )

  const header = useMemo(() => {
    if (customHeader) {
      return (
        <>
          <Space minHeight={24} wide>
            {customHeader}
          </Space>
          {canClose && <Space>{close}</Space>}
        </>
      )
    }
    if (title) {
      return (
        <>
          <Vertical minHeight={24}>
            <Typo
              line={24}
              size={14}
              semi
              accent
              primary
              upper
            >
              {title}
            </Typo>
            {subtitle && <Typo size={14} secondary>
              {subtitle}
            </Typo>}
          </Vertical>
          {canClose && <Space>{close}</Space>}
        </>
      )
    }
    return null
  }, [customHeader, title, canClose])

  const content = <div className={css.content}>
    {children}
  </div>

  return createPortal(
    <div className={className} onMouseDown={onClickShadow}>
      <div ref={setRef} className={css.border}>
        <div className={css.inner}>
          <Space className={clsx(HANDLE, canClose && css.handle)}>
            {canClose && !header && (
              <Space center>
                <div
                  style={{
                    width: 40,
                    height: 4,
                    backgroundColor: 'var(--ui-color-secondary)',
                    borderRadius: 2,
                    opacity: 0.3,
                  }}
                />
              </Space>
            )}
            {header && (
              <Space pad={16} wide top opposite>
                {header}
              </Space>
            )}
          </Space>
          {scroll && (
            <OverlayScrollbarsComponent
              className={clsx(css.scroll, bottom && css.small)}
            >
              {content}
            </OverlayScrollbarsComponent>
          )}
          {!scroll && (
            <Vertical className={clsx(css.scroll, css.no, bottom && css.small)}>
              {content}
            </Vertical>
          )}
          {bottom && (
            <Space className={css.bottom}>
              {bottom}
            </Space>
          )}
        </div>
      </div>
    </div>
    , document.body)
})
